/**
 * resolveProgressLabel — pure formatter mapping the latest
 * inventory-resolve SSE event to a single-line writer-facing status.
 *
 * Shared by the LibraryHub modal and the chat-surface LibraryBar so
 * both surfaces show identical wording while the resolution is in
 * flight. Keeping the formatter in one place avoids copy-paste drift
 * if the event-type union grows.
 */

import type { InventoryResolveEvent } from "../../api/client";

export function resolveProgressLabel(event: InventoryResolveEvent | null | undefined): string {
  if (!event) return "Loading library…";
  switch (event.type) {
    case "listing_documents":
      return typeof event.fetched === "number" && typeof event.total === "number"
        ? `Listing documents ${event.fetched}/${event.total}…`
        : "Listing documents…";
    case "checking_cache":
      return "Checking cache…";
    case "cache_hit":
      return "Cache hit — loading…";
    case "cache_miss":
      return "Classifying documents…";
    case "classifying": {
      const name = event.doc_name;
      const hasCount = typeof event.index === "number" && typeof event.total === "number";
      if (name && hasCount) {
        return `Classifying ${event.index}/${event.total} ${name}…`;
      }
      if (hasCount) return `Classifying ${event.index}/${event.total}…`;
      return name ? `Classifying ${name}…` : "Classifying…";
    }
    case "labelled":
      return event.doc_name ? `Labelled ${event.doc_name}` : "Labelling…";
    case "params_extracting":
      return event.doc_name ? `Extracting params for ${event.doc_name}…` : "Extracting params…";
    case "params_extracted":
      return event.doc_name ? `Extracted params for ${event.doc_name}` : "Params extracted";
    case "done":
      return "Done";
    case "error":
      return "Error loading library";
    default:
      return "Loading library…";
  }
}
